/**
 * Watermark Protection Layer (Layer 1 Protection)
 * 
 * This module provides comprehensive protection for canvas-based watermarks:
 * - DOM mutation monitoring
 * - Style tampering detection
 * - PiP event monitoring
 * - Automatic overlay reinstatement
 * - Backend security event reporting
 */

export interface SecurityEvent {
  eventType: 'dom_tampering' | 'style_manipulation' | 'pip_unauthorized' | 'overlay_removed' | 'devtools_detected' | 'element_hidden';
  userId: string;
  timestamp: number;
  sessionId: string;
  details: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
}

export interface WatermarkProtectionConfig {
  enabled: boolean;
  autoReinstate: boolean;
  reportToBackend: boolean;
  checkInterval: number; // milliseconds
  strictMode: boolean; // If true, take immediate action on tampering
}

export class WatermarkProtectionLayer {
  private protectedElements: Set<HTMLElement> = new Set();
  private mutationObserver: MutationObserver | null = null;
  private integrityCheckInterval: number | null = null;
  private originalStyles: Map<HTMLElement, CSSStyleDeclaration> = new Map();
  private config: WatermarkProtectionConfig;
  private securityEventCallback: ((event: SecurityEvent) => void) | null = null;
  private userId: string;
  private sessionId: string;
  private isActive: boolean = false;
  private tamperingAttempts: number = 0;
  private lastTamperingTime: number = 0;

  constructor(config: Partial<WatermarkProtectionConfig> = {}) {
    this.config = {
      enabled: true,
      autoReinstate: true,
      reportToBackend: true,
      checkInterval: 1000,
      strictMode: true,
      ...config,
    };
    this.userId = '';
    this.sessionId = this.generateSessionId();
  }

  /**
   * Initialize protection for specified elements
   */
  public initialize(elements: HTMLElement[], userId: string, callback?: (event: SecurityEvent) => void): void {
    if (!this.config.enabled) return;

    this.userId = userId;
    this.securityEventCallback = callback || null;
    this.isActive = true;

    // Protect each element
    elements.forEach((element) => {
      this.protectElement(element);
    });

    // Start monitoring
    this.startMutationObserver();
    this.startIntegrityChecks();

  }

  /**
   * Protect a single element
   */
  private protectElement(element: HTMLElement): void {
    if (!element) return;

    // Store original styles
    const computedStyle = window.getComputedStyle(element);
    this.originalStyles.set(element, computedStyle);

    // Add to protected set
    this.protectedElements.add(element);

    // Add protection attributes
    element.setAttribute('data-protected', 'true');
    element.setAttribute('data-protection-id', this.generateProtectionId());

    // Prevent common tampering attempts
    this.addElementProtection(element);
  }

  /**
   * Add JavaScript-based protection to element
   */
  private addElementProtection(element: HTMLElement): void {
    // Prevent style manipulation via Object.defineProperty
    const originalStyle = element.style;
    
    // Monitor style changes
    const styleProxy = new Proxy(originalStyle, {
      set: (target: any, property: string, value: any) => {
        if (this.isStyleTampering(property, value)) {
          this.reportSecurityEvent({
            eventType: 'style_manipulation',
            details: `Attempted to change ${property} to ${value}`,
            severity: 'high',
          });
          
          if (this.config.strictMode) {
            return false; // Block the change
          }
        }
        target[property] = value;
        return true;
      },
    });

    // Prevent removal via removeChild/remove
    const originalRemove = element.remove;
    element.remove = () => {
      this.reportSecurityEvent({
        eventType: 'overlay_removed',
        details: 'Attempted to remove protected element via .remove()',
        severity: 'critical',
      });

      if (this.config.autoReinstate) {
        return;
      }
      originalRemove.call(element);
    };
  }

  /**
   * Start DOM mutation observer
   */
  private startMutationObserver(): void {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        this.handleMutation(mutation);
      });
    });

    // Observe the entire document for changes
    this.mutationObserver.observe(document.body, {
      childList: true,
      attributes: true,
      attributeOldValue: true,
      subtree: true,
      characterData: false,
    });
  }

  /**
   * Handle detected mutations
   */
  private handleMutation(mutation: MutationRecord): void {
    // Check if any protected element was removed
    if (mutation.type === 'childList' && mutation.removedNodes.length > 0) {
      mutation.removedNodes.forEach((node) => {
        if (node instanceof HTMLElement && this.protectedElements.has(node)) {
          this.reportSecurityEvent({
            eventType: 'overlay_removed',
            details: 'Protected element removed from DOM',
            severity: 'critical',
          });

          if (this.config.autoReinstate) {
            this.reinstateElement(node, mutation.target as HTMLElement);
          }
        }
      });
    }

    // Check if protected element attributes were modified
    if (mutation.type === 'attributes' && mutation.target instanceof HTMLElement) {
      if (this.protectedElements.has(mutation.target)) {
        const attributeName = mutation.attributeName;
        
        if (attributeName === 'style' || attributeName === 'class') {
          this.reportSecurityEvent({
            eventType: 'style_manipulation',
            details: `${attributeName} attribute modified on protected element`,
            severity: 'high',
          });

          if (this.config.autoReinstate) {
            this.restoreElementIntegrity(mutation.target);
          }
        }
      }
    }
  }

  /**
   * Start periodic integrity checks
   */
  private startIntegrityChecks(): void {
    this.integrityCheckInterval = window.setInterval(() => {
      this.performIntegrityCheck();
    }, this.config.checkInterval);
  }

  /**
   * Perform integrity check on all protected elements
   */
  private performIntegrityCheck(): void {
    if (!this.isActive) return;

    this.protectedElements.forEach((element) => {
      if (!document.body.contains(element)) {
        // Element is no longer in DOM
        this.reportSecurityEvent({
          eventType: 'overlay_removed',
          details: 'Protected element no longer in DOM (integrity check)',
          severity: 'critical',
        });
      } else {
        // Check visibility
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
          this.reportSecurityEvent({
            eventType: 'element_hidden',
            details: `Element hidden via CSS: display=${style.display}, visibility=${style.visibility}, opacity=${style.opacity}`,
            severity: 'high',
          });

          if (this.config.autoReinstate) {
            this.restoreElementIntegrity(element);
          }
        }
      }
    });
  }

  /**
   * Reinstate removed element
   */
  private reinstateElement(element: HTMLElement, parent: HTMLElement): void {
    try {
      parent.appendChild(element);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Restore element integrity
   */
  private restoreElementIntegrity(element: HTMLElement): void {
    // Restore critical styles
    element.style.display = '';
    element.style.visibility = 'visible';
    element.style.opacity = '';
    element.style.position = 'absolute';
    element.style.zIndex = '';
  }

  /**
   * Check if style change is considered tampering
   */
  private isStyleTampering(property: string, value: any): boolean {
    const suspiciousChanges = [
      { prop: 'display', values: ['none'] },
      { prop: 'visibility', values: ['hidden', 'collapse'] },
      { prop: 'opacity', values: ['0'] },
      { prop: 'zIndex', values: ['-1', '-999', '-9999'] },
    ];

    return suspiciousChanges.some(
      (change) => property === change.prop && change.values.includes(String(value))
    );
  }

  /**
   * Report security event
   */
  private reportSecurityEvent(partial: Omit<SecurityEvent, 'userId' | 'timestamp' | 'sessionId'>): void {
    // Rate limiting
    const now = Date.now();
    if (now - this.lastTamperingTime < 100) {
      return; // Avoid spam
    }

    this.lastTamperingTime = now;
    this.tamperingAttempts++;

    const event: SecurityEvent = {
      ...partial,
      userId: this.userId,
      timestamp: now,
      sessionId: this.sessionId,
    };

    // Call callback if provided
    if (this.securityEventCallback && this.config.reportToBackend) {
      this.securityEventCallback(event);
    }

    // Take action in strict mode
    if (this.config.strictMode && this.tamperingAttempts > 5) {
      // You could trigger additional actions here (logout, stream cutoff, etc.)
    }
  }

  /**
   * Monitor PiP events
   */
  public monitorPiPEvents(videoElement: HTMLVideoElement): void {
    videoElement.addEventListener('enterpictureinpicture', () => {
      this.reportSecurityEvent({
        eventType: 'pip_unauthorized',
        details: 'User entered Picture-in-Picture mode',
        severity: 'medium',
      });
    });

    videoElement.addEventListener('leavepictureinpicture', () => {});
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<WatermarkProtectionConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Destroy protection layer
   */
  public destroy(): void {
    this.isActive = false;

    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }

    if (this.integrityCheckInterval) {
      clearInterval(this.integrityCheckInterval);
      this.integrityCheckInterval = null;
    }

    this.protectedElements.clear();
    this.originalStyles.clear();
  }

  /**
   * Generate unique session ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Generate unique protection ID
   */
  private generateProtectionId(): string {
    return `prot_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get statistics
   */
  public getStatistics(): { tamperingAttempts: number; protectedElements: number; isActive: boolean } {
    return {
      tamperingAttempts: this.tamperingAttempts,
      protectedElements: this.protectedElements.size,
      isActive: this.isActive,
    };
  }
}

